import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Stack,
  Text,
  Heading,
  Button,
  Image,
  Flex,
} from "@chakra-ui/react";
import { FiArrowRight, FiCalendar, FiUsers, FiBookOpen } from "react-icons/fi";

const participationAreas = [
  {
    number: "01",
    icon: FiUsers,
    title: "Scientific Exchange",
    text:
      "Seminars, expert conversations and cross-disciplinary discussions that bring researchers, clinicians, innovators and health leaders into the same room.",
  },
  {
    number: "02",
    icon: FiArrowRight,
    title: "Collaboration",
    text:
      "Research collaborations, implementation projects and connections between people and institutions working on related problems.",
  },
  {
    number: "03",
    icon: FiUsers,
    title: "Emerging Talent",
    text:
      "Opportunities for students, early-career researchers and professionals developing the skills and relationships needed to contribute to African health science.",
  },
  {
    number: "04",
    icon: FiBookOpen,
    title: "Knowledge",
    text:
      "Practical briefs, concept notes and other resources emerging from Serena's scientific and translational work.",
  },
];

function SectionLabel({ children }) {
  return (
    <Text
      fontSize="xs"
      fontWeight="700"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="#087F73"
      mb={4}
    >
      {children}
    </Text>
  );
}

function CommunityPage() {
  return (
    <Box>
      {/* HERO */}
      <Box
        bg="#F3F5F1"
        py={{ base: 20, md: 28, lg: 36 }}
        overflow="hidden"
      >
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Box maxW="900px">
            <SectionLabel>THE SERENA COMMUNITY</SectionLabel>

            <Heading
              as="h1"
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
              lineHeight="1.05"
              letterSpacing="-0.045em"
              fontWeight="700"
              color="#17211F"
            >
              A community for people building the future of preventive and
              precision health in Africa.
            </Heading>

            <Text
              mt={7}
              maxW="760px"
              fontSize={{ base: "lg", md: "xl" }}
              lineHeight="1.7"
              color="#4A5753"
            >
              Serena brings together people working across science,
              technology and healthcare to exchange knowledge, develop
              collaborations and move promising ideas closer to practice.
            </Text>
          </Box>
        </Container>
      </Box>

      {/* WHY COMMUNITY */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 24 }}
          >
            <Box>
              <SectionLabel>WHY IT MATTERS</SectionLabel>

              <Heading
                as="h2"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
                maxW="620px"
              >
                The work between evidence and action is rarely done by one
                discipline.
              </Heading>
            </Box>

            <Stack spacing={6} maxW="680px">
              <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                Researchers generate evidence. Clinicians understand the
                realities of care. Technologists build tools. Institutions
                create the conditions for those tools to be used.
              </Text>

              <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.8">
                Serena's community exists to create more opportunities for
                these perspectives to meet, learn from one another and work
                together.
              </Text>

              <Text
                fontSize={{ base: "lg", md: "xl" }}
                lineHeight="1.7"
                fontWeight="600"
                color="#087F73"
              >
                Scientific exchange is part of the infrastructure.
              </Text>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* PARTICIPATION */}
      <Box bg="#F9FAF8" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SectionLabel>HOW TO ENGAGE</SectionLabel>

          <Heading
            as="h2"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "2xl", md: "4xl" }}
            lineHeight="1.15"
            letterSpacing="-0.035em"
            fontWeight="600"
            maxW="760px"
            mb={{ base: 12, md: 16 }}
          >
            Find your way into the work.
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {participationAreas.map((item) => {
              const Icon = item.icon;

              return (
                <Box
                  key={item.number}
                  bg="white"
                  p={{ base: 7, md: 9 }}
                  minH={{ md: "250px" }}
                  border="1px solid"
                  borderColor="blackAlpha.100"
                >
                  <Flex
                    justify="space-between"
                    align="flex-start"
                    mb={8}
                  >
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      letterSpacing="0.1em"
                      color="#087F73"
                    >
                      {item.number}
                    </Text>

                    <Icon size={20} />
                  </Flex>

                  <Heading
                    as="h3"
                    fontSize={{ base: "xl", md: "2xl" }}
                    letterSpacing="-0.025em"
                    mb={4}
                  >
                    {item.title}
                  </Heading>

                  <Text color="#596560" lineHeight="1.75">
                    {item.text}
                  </Text>
                </Box>
              );
            })}
          </SimpleGrid>
        </Container>
      </Box>

      {/* COMMUNITY IN MOTION */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 10, lg: 20 }}
            alignItems="center"
          >
            <Box
              overflow="hidden"
              bg="#E8ECE7"
              aspectRatio={{ base: 4 / 3, lg: 4 / 5 }}
            >
              <Image
                src="/images/institutional/serena3.png"
                alt="African researchers, clinicians and innovators in a scientific seminar"
                w="100%"
                h="100%"
                objectFit="cover"
              />
            </Box>

            <Box maxW="650px">
              <SectionLabel>COMMUNITY IN MOTION</SectionLabel>

              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="#7256A3"
                mb={3}
              >
                August 2026 · Serena Space Institute
              </Text>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                mb={5}
              >
                Building Africa's Precision Health Future
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#596560"
                mb={6}
              >
                Serena's first Institute seminar brought together perspectives
                on genomics, data science, artificial intelligence and clinical
                practice around a shared question: how can African health
                science move closer to measurable impact?
              </Text>

              <Flex
                direction={{ base: "column", sm: "row" }}
                gap={4}
                align={{ base: "stretch", sm: "center" }}
              >
                <Button
                  as="a"
                  href="/institute"
                  variant="outline"
                  size="md"
                  rightIcon={<FiArrowRight />}
                  borderColor="#087F73"
                  color="#087F73"
                  _hover={{
                    bg: "#087F73",
                    color: "white",
                  }}
                >
                  Explore the Institute
                </Button>
              </Flex>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* WHAT COMMUNITY PRODUCES */}
      <Box bg="#17211F" color="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 24 }}
          >
            <Box>
              <SectionLabel>BEYOND CONVERSATION</SectionLabel>

              <Heading
                as="h2"
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "2xl", md: "4xl" }}
                lineHeight="1.15"
                letterSpacing="-0.035em"
                fontWeight="600"
                maxW="620px"
              >
                Exchange should leave something behind.
              </Heading>
            </Box>

            <Stack spacing={7}>
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.1em"
                  color="whiteAlpha.500"
                  mb={2}
                >
                  KNOWLEDGE
                </Text>
                <Text fontSize="lg" lineHeight="1.7">
                  Implementation briefs, concept notes and other practical
                  outputs that make ideas easier to act on.
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.1em"
                  color="whiteAlpha.500"
                  mb={2}
                >
                  CONNECTION
                </Text>
                <Text fontSize="lg" lineHeight="1.7">
                  Relationships between people and institutions with
                  complementary expertise, capabilities and interests.
                </Text>
              </Box>

              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="700"
                  letterSpacing="0.1em"
                  color="whiteAlpha.500"
                  mb={2}
                >
                  ACTION
                </Text>
                <Text fontSize="lg" lineHeight="1.7">
                  Conversations that can lead to research, pilots,
                  implementation opportunities and new evidence.
                </Text>
              </Box>
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Box bg="white" py={{ base: 20, md: 28 }}>
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={8}
            borderTop="1px solid"
            borderColor="blackAlpha.100"
            pt={{ base: 10, md: 14 }}
          >
            <Box maxW="700px">
              <SectionLabel>JOIN THE CONVERSATION</SectionLabel>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "4xl" }}
                letterSpacing="-0.035em"
              >
                Bring your expertise, question or idea.
              </Heading>

              <Text
                mt={4}
                color="#596560"
                fontSize={{ base: "md", md: "lg" }}
              >
                Tell us what you are working on and where you see an
                opportunity to collaborate.
              </Text>
            </Box>

            <Button
              as="a"
              href="/partner"
              size="lg"
              px={7}
              bg="#087F73"
              color="white"
              rightIcon={<FiArrowRight />}
              _hover={{
                bg: "#066B61",
                textDecoration: "none",
              }}
            >
              Connect with Serena
            </Button>
          </Flex>
        </Container>
      </Box>
    </Box>
  );
}

export default CommunityPage;